import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { SchoolYearDates as SchoolYearDatesList } from "../../components/site/SchoolYearDates";
import { SchoolYearLegacy } from "@/components/site/SchoolYearLegacy";
import { schoolTermsQuery } from "@/lib/school-calendar-query";
import { safeLoader } from "@/lib/safe-loader";

const title = "School year dates 2026–2027 | Swedish International School";
const description =
  "Term dates, holidays and public holidays for school, preschool and leisure activities at Swedish International School Estepona – Marbella.";

export const Route = createFileRoute("/en/school-year-dates")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/school-year-dates") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/school-year-dates"),
  }),
  loader: ({ context }) => safeLoader("en.school-terms", () => context.queryClient.ensureQueryData(schoolTermsQuery("en")), { terms: [], fetchedAt: null }),
  component: SchoolYearDates,
});

function SchoolYearDates() {
  const terms = Route.useLoaderData().terms;

  return (
    <>
      <PageHero
        eyebrow="Calendar"
        title="School year dates"
        intro="The dates and holidays below apply to school, preschool and leisure activities. On holidays and public holidays, school, preschool and leisure activities are closed."
      />

      <SchoolYearDatesList locale="en" terms={terms} />

      <SchoolYearLegacy />


      <Prose>
        <h2>How the school year is structured</h2>
        <p>
          The school year is divided into an autumn term and a spring term, with holidays that follow
          both Swedish school tradition and local public holidays in Andalusia. The dates above apply
          to school, preschool and after-school care, and are updated when the school confirms the
          calendar for the coming year.
        </p>

        <h2>Starting mid-term</h2>
        <p>
          Applications are accepted throughout the school year. When starting mid-term we review where
          the pupil is in the syllabus and plan the introduction together with you. In the{" "}
          <a className="text-primary link-quiet" href="/en/application">application</a> you choose
          which term you wish to start, and the start date is set to the first school day of that term.
        </p>

        <h2>Leave and absence</h2>
        <p>
          Report sick leave to the school in the morning. If your child needs leave outside the
          holidays, apply to the class teacher in good time so work can be planned ahead. We recommend
          placing longer trips during the holidays, since much of the teaching builds on shared work in
          the classroom.
        </p>

        <h2>Questions about the calendar</h2>
        <p>
          If a date is unclear, or you need to plan a move around the school calendar, email{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">info@sweintschool.com</a>{" "}
          and we'll help. Practical tips ahead of a move are collected in our{" "}
          <a className="text-primary link-quiet" href="/en/guides">guides</a>.
        </p>
      </Prose>

      <CtaBand locale="en" />
      <div className="h-20" />
    </>
  );
}
